import { useState } from "react";
import {
  Alert, Box, Button, CircularProgress, LinearProgress,
  TextField, Typography,
} from "@mui/material";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import SearchIcon from "@mui/icons-material/Search";
import SpeedIcon from "@mui/icons-material/Speed";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CommonPage from "../../components/common-page";
import useLanguage from "../../hooks/useLanguage";
import useContent from "../../hooks/useContent";
import SeoScores from "./components/SeoScores";
import ReportFindings from "./components/ReportFindings";
import ComingSoonTeaser from "./components/ComingSoonTeaser";

const PILLARS = [
  { icon: SpeedIcon,            labelKey: "pillarTechnical" },
  { icon: ArticleOutlinedIcon,  labelKey: "pillarContent"   },
  { icon: SmartToyOutlinedIcon, labelKey: "pillarGeo"       },
];

const AGENTS = [
  { icon: TravelExploreIcon,    label: "Fetching page",        signals: 0  },
  { icon: SpeedIcon,            label: "SEO Technical",        signals: 24 },
  { icon: ArticleOutlinedIcon,  label: "Content Quality",      signals: 16 },
  { icon: SmartToyOutlinedIcon, label: "GEO / AI Visibility",  signals: 19 },
  { icon: SpeedIcon,            label: "PageSpeed",            signals: 8  },
  { icon: CompareArrowsIcon,    label: "Competitor Context",   signals: 0  },
];

const TOTAL_SIGNALS = AGENTS.reduce((sum, a) => sum + a.signals, 0);

function EmptyState({ text }) {
  return (
    <Box
      sx={{
        display: "flex", flexDirection: "column", alignItems: "center",
        textAlign: "center", py: 8, px: 2, gap: 3,
      }}
    >
      {/* Pulsing radar icon */}
      <Box sx={{ position: "relative", width: 80, height: 80 }}>
        {[1, 2].map(i => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              inset: `-${i * 18}px`,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: `rgba(128,128,128,${0.2 - i * 0.07})`,
              "@keyframes radarPulse": {
                "0%":   { opacity: 0.7, transform: "scale(1)" },
                "100%": { opacity: 0,   transform: "scale(1.25)" },
              },
              animation: `radarPulse ${1.8 + i * 0.6}s ease-out infinite`,
              animationDelay: `${i * 0.4}s`,
            }}
          />
        ))}
        <Box sx={{
          width: 80, height: 80, borderRadius: "50%",
          bgcolor: "action.hover",
          border: "1px solid", borderColor: "divider",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <SearchIcon sx={{ fontSize: 34, color: "text.secondary" }} />
        </Box>
      </Box>

      {/* Headline with gradient text */}
      <Box>
        <Typography
          variant="h5"
          fontWeight={800}
          gutterBottom
          sx={{ letterSpacing: "-0.02em" }}
        >
          {text.emptyTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, lineHeight: 1.7 }}>
          {text.emptySubtitle.replace("{n}", TOTAL_SIGNALS)}
        </Typography>
      </Box>

      {/* Pillar chips */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
        {PILLARS.map(({ icon: Icon, labelKey }) => (
          <Box
            key={labelKey}
            sx={{
              display: "flex", alignItems: "center", gap: 1,
              px: 2, py: 1, borderRadius: 2,
              border: "1px solid", borderColor: "divider",
              transition: "border-color 0.2s",
              "&:hover": { borderColor: "text.disabled" },
            }}
          >
            <Icon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {text[labelKey]}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function AnalyzingState({ text }) {
  return (
    <Box sx={{ mb: 3 }}>
      <LinearProgress
        sx={{
          mb: 2.5, borderRadius: 1, height: 3,
          bgcolor: "action.hover",
          "& .MuiLinearProgress-bar": { bgcolor: "text.primary" },
        }}
      />

      {/* Agent pills */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
        {AGENTS.map(({ icon: Icon, label, signals }) => (
          <Box
            key={label}
            sx={{
              display: "flex", alignItems: "center", gap: 1,
              px: 2, py: 0.75, borderRadius: "20px",
              border: "1px solid", borderColor: "divider",
              "@keyframes agentPulse": {
                "0%, 100%": { opacity: 0.55 },
                "50%":      { opacity: 1 },
              },
              animation: "agentPulse 1.8s ease-in-out infinite",
            }}
          >
            <Box sx={{
              width: 6, height: 6, borderRadius: "50%", bgcolor: "text.secondary", flexShrink: 0,
              "@keyframes dotBounce": {
                "0%, 100%": { transform: "scale(0.7)" },
                "50%":      { transform: "scale(1.3)" },
              },
              animation: "dotBounce 1.2s ease-in-out infinite",
            }} />
            <Typography variant="caption" fontWeight={500} color="text.secondary">
              {signals > 0 ? `${label} — ${signals} signals` : label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography variant="caption" color="text.disabled">
        {text.analyzing.replace("{n}", TOTAL_SIGNALS)}
      </Typography>
    </Box>
  );
}

export default function SeoPage() {
  const [{ pages: { seo: text } }] = useLanguage();
  const { generateFromUrl } = useContent();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [urlError, setUrlError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  const handleCategoryClick = (category) =>
    setActiveCategory(prev => prev === category ? null : category);

  const handleAnalyze = async () => {
    if (!url) return;
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      setUrlError("URL must start with http:// or https://");
      return;
    }
    setLoading(true);
    setReport(null);
    setError(null);
    try {
      const token = localStorage.getItem("x-token");
      const res = await fetch(`/api/seo/analyze?url=${encodeURIComponent(url)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const t = await res.text();
        let msg = "Analysis failed";
        try { msg = JSON.parse(t).message || msg; } catch (_) {}
        throw new Error(res.status === 401 ? "Unauthorized — please log in again" : msg);
      }
      const data = await res.json();
      setReport(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const signalCount = report?.checks?.length ?? TOTAL_SIGNALS;

  return (
    <CommonPage title={text.title} subtitle={text.subtitle.replace("{n}", signalCount)}>
      {/* URL input */}
      <Box sx={{ display: "flex", gap: 1, mb: urlError ? 0.5 : 3 }}>
        <TextField
          fullWidth
          value={url}
          onChange={(e) => { setUrl(e.target.value); setUrlError(null); }}
          placeholder={text.urlPlaceholder}
          disabled={loading}
          onKeyDown={(e) => e.key === "Enter" && handleAnalyze()}
          error={!!urlError}
          InputProps={{
            startAdornment: (
              <TravelExploreIcon sx={{ mr: 1, color: "text.secondary", flexShrink: 0 }} />
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "text.primary",
                borderWidth: 1.5,
              },
            },
          }}
        />
        <Button
          variant="contained"
          onClick={handleAnalyze}
          disabled={loading || !url.trim()}
          sx={{
            minWidth: 120, textTransform: "none", fontWeight: 700,
            bgcolor: "text.primary", color: "background.paper",
            "&:hover": { bgcolor: "text.secondary" },
            "&:disabled": { opacity: 0.4 },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : text.analyze}
        </Button>
      </Box>

      {urlError && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 2 }}>
          {urlError}
        </Typography>
      )}

      {/* Analyzing */}
      {loading && <AnalyzingState text={text} />}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Empty state */}
      {!report && !loading && !error && (
        <EmptyState text={text} />
      )}

      {/* Results */}
      {report && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <SeoScores
            report={report}
            text={text.scores}
            activeCategory={activeCategory}
            onCategoryClick={handleCategoryClick}
          />

          {report.quick_wins?.length > 0 && (
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                {text.quickWins.title}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {report.quick_wins.map((win, i) => (
                  <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {win}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          <ReportFindings
            checks={report.checks}
            priorityFixes={report.priority_fixes}
            activeCategory={activeCategory}
            onClearCategory={() => setActiveCategory(null)}
            text={text}
          />
          <ComingSoonTeaser
            text={text.teaser}
            report={report}
            onRewrite={() => generateFromUrl({ url, geoReport: report })}
          />
        </Box>
      )}
    </CommonPage>
  );
}
